import { createCookie } from '@shared/cookies/createCookie';
import { Maker } from './buildMachine';
import { encodeToBase64 } from '@shared/conversion/encodeToBase64';
import { getBaseDomain } from '@shared/domain/getBaseDomain';
import { Ctx, Snap } from './core';
import { buildUrl } from './utils';

export const consentDenied: Maker<Ctx, Snap, null> = ({
  makeEffect,
  makeEffectGroup,
}) => [
  makeEffectGroup(
    'localStorageRemove',
    (_, { pass }) => pass(null),
    ({ makeEffect }) =>
      (
        [
          '_GESPSK-utiq.com',
          'utiqDataCallTimestamp',
          'utiqPass',
          'utiqStub',
          'utiq_openrtb_eids',
          'utiq_popup_ask_me_later',
        ] as const
      ).map((localStorageKey) =>
        makeEffect(
          localStorageKey,
          (_, { snap, cond }) => cond(snap.localStorage[localStorageKey], ''),
          (_, { ctx }) => ctx.window.localStorage.removeItem(localStorageKey),
        ),
      ),
  ),

  makeEffectGroup(
    'cookiesRemove',
    (_, { pass }) => pass(null),
    ({ makeEffect }) =>
      (
        [
          'utiq_consent_version',
          'utiq_consent_version_previous',
          // 'utiq_logging_level',
        ] as const
      ).map((cookieKey) =>
        makeEffect(
          cookieKey,
          (_, { ctx, snap, pass, skip }) =>
            !!snap.cookies[cookieKey]
              ? pass(
                  createCookie({
                    name: cookieKey,
                    value: encodeToBase64(false),
                    days: 0,
                    domain: getBaseDomain(ctx.window.location.hostname),
                  }),
                )
              : skip(''),

          (cookie, { ctx }) => {
            ctx.window.document.cookie = cookie;
          },
        ),
      ),
  ),

  makeEffect(
    'makeDataCall',
    (_, { ctx, skip, pass }) => {
      if (
        !ctx.axios.log.find(
          (x) => x.req.url && new URL(x.req.url).pathname.endsWith('/consent'),
        )
      )
        return skip('consent not made yet');

      if (!ctx.axios.active[0]) return skip();

      const { res, req } = ctx.axios.active[0];

      if (!req?.url) return skip();

      if (!new URL(req.url).pathname.endsWith('/consent'))
        return skip('no preceding consent req');

      if (!res || !res.data) return skip();

      const consentState = (
        Object.values(res.data)?.[0] as { consentState?: any }
      ).consentState;
      if (consentState !== 'rejected') return skip('consentState !== rejected');

      if (ctx.times.handleConsentRepromptRequirement)
        return skip('handleConsentRepromptRequirement');

      const url = buildUrl(ctx, ['idConnectConfig'])
        ?.tryPathFrom(
          ctx.window.Utiq?.data?.clientConfig?.IDCONNECT_CONFIG
            ?.IDCONNECT_API_HOST,
        )
        ?.addPathSegment('data')
        .withTransactionId()
        .render();

      if (!url) return skip('url unbuildable');

      if (ctx.axios.active.find((x) => x.req.url === url))
        return skip('active');

      return pass(url);
    },
    (url, { ctx }) => {
      ctx.axios.active.shift();
      ctx.axios.instance({
        method: 'get',
        url,
        withCredentials: true,
        timeout: 4000,
      });
    },
  ),
];
